import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight, ArrowRight, HelpCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getExchangeLink, getFollowLink, getExchangeAffiliateLink } from "@/utils/exchangeUtils";
import { useState, useEffect } from "react";
import ExchangeVideoGuide from '@/components/follow-strategy/ExchangeVideoGuide';
import { useIsMobile } from "@/hooks/use-mobile";
import ChatOption from "@/components/ChatOption";

const ThankYou = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const strategy = location.state?.strategy;
  const isYearly = location.state?.isYearly;
  const [exchangeLink, setExchangeLink] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [affiliateLink, setAffiliateLink] = useState("");

  useEffect(() => {
    const loadExchangeLink = async () => {
      if (strategy?.id) {
        try {
          const link = await getFollowLink(strategy.id);
          setExchangeLink(link);
        } catch (error) {
          console.error("Error loading exchange link:", error);
          // Fallback to static link
          setExchangeLink(getExchangeLink(strategy?.exchange || 'okx'));
        } finally {
          setIsLoading(false);
        }
      } else {
        setExchangeLink(getExchangeLink(strategy?.exchange || 'okx'));
        setIsLoading(false);
      }

      // Set affiliate link
      setAffiliateLink(getExchangeAffiliateLink(strategy?.exchange || 'okx'));
    };

    loadExchangeLink();
  }, [strategy]);

  const isMobile = useIsMobile();

  const handleExchangeRedirect = () => {
    if (exchangeLink) {
      window.open(exchangeLink, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          <div className="flex justify-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="bg-green-100 p-4 rounded-full"
            >
              <Check className="w-12 h-12 text-green-600" />
            </motion.div>
          </div>

          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-gray-800">
              Thank You for Subscribing to METRADE Premium!
            </h1>
            <p className="text-xl text-gray-600">
              Your journey to advanced crypto trading starts now.
            </p>
          </div>

          <Card className="p-6 bg-blue-50 border-blue-200">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-gray-800">
                Premium Subscription Confirmed
              </h2>
              <p className="text-gray-600">
                You have successfully subscribed to{" "}
                <span className="font-medium">{strategy?.name || "Premium Strategy"}</span>
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Performance: +31% in last 30 days</span>
              </div>
            </div>
          </Card>

          <div className="group bg-gradient-to-br from-[#FF3B3B] to-[#D72631] p-1 rounded-lg shadow-md">
            <Card className="p-6 bg-white rounded-md">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 rounded-full bg-[#FF3B3B]/10 flex items-center justify-center mr-4 group-hover:bg-[#FF3B3B]/20 transition-colors duration-300">
                  <HelpCircle className="w-7 h-7 text-[#FF3B3B]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold group-hover:text-[#FF3B3B] transition-colors">
                    What Comes Next?
                  </h3>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-3">If you already have an exchange account:</h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Log in to your exchange and confirm the copy-trading setup</li>
                    <li>Enable necessary permissions for METRADE Premium strategy execution</li>
                    <li>Ensure sufficient balance for minimum investment requirements</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-3">If you're new to {strategy?.exchange || "the exchange"}:</h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>
                      <a
                        href={affiliateLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors duration-200"
                      >
                        Create a new account on {strategy?.exchange || "the exchange"}
                      </a>
                    </li>
                    <li>Complete the KYC verification process if required</li>
                    <li>Return to this page to activate your METRADE Premium strategy</li>
                  </ul>
                </div>

                <ExchangeVideoGuide exchange={strategy?.exchange || 'OKX'} />
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-4">Finalize Setup on Your Exchange</h2>
              <p className="text-gray-600 mb-6">
                Click below to finalize whitelisting or set up copy trading for your new METRADE Premium strategy.
              </p>
              <Button
                onClick={handleExchangeRedirect}
                className="bg-[#007BFF] hover:bg-[#0056b3] text-white w-full sm:w-auto py-6 px-8"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <span className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></span>
                    Loading...
                  </span>
                ) : (
                  <>
                    Continue to {strategy?.exchange || "Exchange"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <div className="text-center space-y-4">
              <p className="text-sm text-gray-500">
                Having trouble? Our METRADE Premium support team is here to help 24/7
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/account')}
                  className="text-gray-600"
                >
                  Go to My Account
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/support')}
                  className="text-gray-600"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
      {/* Only show ChatOption on desktop */}
      {!isMobile && (
        <ChatOption />
      )}
      <Footer />
    </div>
  );
};

export default ThankYou;
